import React, { useState, useEffect } from 'react';
import { Select, InputNumber, Alert, Spin, Card, Input } from 'antd';
import Icon, {
  LoadingOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  KeyOutlined,
  RocketOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import RoundedModal from '@components/roundedModal/roundedModal';
import useTranslation from '@hooks/useTranslation';
import useInvokeHubFunction from '@common/hooks/useInvokeHubFunction';
import {
  GetHubSecretsResponse,
  HubEnvVarInfo,
} from '@src/types/secretMapping_t';
import { KemuHubFunctions } from '@kemu-io/hs-types';
import styles from './DeployRecipeModal.module.css';
import {
  selectDeployRecipeModalVisible,
  showDeployRecipeModal,
} from '@src/features/interface/interfaceSlice';
import { selectCurrentRecipe } from '@src/features/Workspace/workspaceSlice';
import deploymentApi from '@src/api/deployment/deploymentApi';
import { deployRecipeAction } from '@src/features/deployment/deploymentReducer';
import { ReactComponent as ServerIcon } from '@src/assets/img/interface/server.svg';

import { AppDispatch } from '@src/app/store';

const { Option } = Select;

/**
 * Modal component for deploying recipes to target environments
 */
const DeployRecipeModal: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const visible = useSelector(selectDeployRecipeModalVisible);
  const currentRecipe = useSelector(selectCurrentRecipe);
  const t = useTranslation('DeployRecipeModal');
  const tCommon = t.withBaseKey('CommonWords');
  const invokeHubFunction = useInvokeHubFunction();
  const recipeUuid = currentRecipe.uuid;

  const [loading, setLoading] = useState(false);
  const [environments, setEnvironments] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [hubSecrets, setHubSecrets] = useState<Record<
    string,
    HubEnvVarInfo[]
  > | null>(null);
  const [selectedEnvironmentId, setSelectedEnvironmentId] =
    useState<string>('');
  const [replicas, setReplicas] = useState<number>(1);
  const [encryptionContext, setEncryptionContext] = useState<string>('');
  const [encryptionKey, setEncryptionKey] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [deployError, setDeployError] = useState<string | null>(null);
  const [deploySuccess, setDeploySuccess] = useState<boolean>(false);

  /**
   * Loads environments and hub secrets
   */
  const loadData = async () => {
    if (!recipeUuid) {
      return;
    }

    setLoading(true);
    try {
      // Fetch environments
      const envs = await deploymentApi.listEnvironments();
      setEnvironments(envs);

      // Get available hub secrets from the hub
      const hubData = await invokeHubFunction<GetHubSecretsResponse>(
        KemuHubFunctions.GetHubSecrets,
        [{ recipeUuid }],
      );
      if (hubData && Array.isArray(hubData) && hubData.length > 0) {
        const secrets = hubData[0].contexts;
        setHubSecrets(secrets);
      }
    } catch (error) {
      console.error('Failed to load deployment data:', error);
      setDeployError(error.message || 'Failed to load deployment data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gets available contexts from hub secrets
   */
  const getAvailableContexts = (): string[] => {
    if (!hubSecrets) {
      return [];
    }
    return Object.keys(hubSecrets);
  };

  /**
   * Gets available environment variables for a specific context
   */
  const getAvailableEnvVars = (context: string): HubEnvVarInfo[] => {
    if (!hubSecrets || !context) {
      return [];
    }
    return hubSecrets[context] || [];
  };

  /**
   * Handles context change and resets secret key
   */
  const handleContextChange = (context: string) => {
    setEncryptionContext(context);
    setEncryptionKey('');
  };

  /**
   * Handles form submission
   */
  const handleOk = async () => {
    if (!selectedEnvironmentId) {
      setDeployError(t('Error.EnvironmentRequired'));
      return;
    }

    if (!currentRecipe.entityInfo?.dbId) {
      setDeployError(t('Error.RecipeNotSaved'));
      return;
    }

    setLoading(true);
    setDeployError(null);
    setDeploySuccess(false);

    try {
      await dispatch(
        deployRecipeAction({
          environmentId: selectedEnvironmentId,
          replicas,
          encryptionContext: encryptionContext || null,
          encryptionKey: encryptionKey || null,
          notes: notes || undefined,
        }),
      ).unwrap();

      setDeploySuccess(true);
      // Close the modal after a short delay
      setTimeout(() => {
        dispatch(showDeployRecipeModal(false));
        handleCancel();
      }, 1500);
    } catch (error) {
      console.error('Failed to deploy recipe:', error);
      setDeployError(error.message || 'Failed to deploy recipe');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles modal cancellation
   */
  const handleCancel = () => {
    dispatch(showDeployRecipeModal(false));
    setDeploySuccess(false);
    setDeployError(null);
    setSelectedEnvironmentId('');
    setReplicas(1);
    setEncryptionContext('');
    setEncryptionKey('');
    setNotes('');
  };

  // Load data when modal becomes visible
  useEffect(() => {
    if (visible) {
      loadData();
    }
  }, [visible, recipeUuid]);

  const availableContexts = getAvailableContexts();
  const availableEnvVars = getAvailableEnvVars(encryptionContext);

  return (
    <>
      <RoundedModal
        visible={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        title={
          <div className={styles.TitleContainer}>
            <RocketOutlined className={styles.TitleIcon} />
            <span>{t('Title')}</span>
          </div>
        }
        width={600}
        okBtnLabel={
          <span>
            <ThunderboltOutlined /> {t('DeployButton')}
          </span>
        }
        cancelBtnLabel={tCommon('Cancel')}
        disableOkButton={loading || !selectedEnvironmentId}
        closeOnMaskClick={false}
      >
        <div className={styles.Container}>
          {deployError && (
            <Alert
              description={deployError}
              type='error'
              showIcon
              closable
              onClose={() => setDeployError(null)}
              className={styles.ErrorAlert}
              style={{ marginBottom: 16 }}
            />
          )}
          {deploySuccess && (
            <Alert
              message={t('Success.DeploymentCreated')}
              description={t('Success.DeploymentCreatedDescription')}
              type='success'
              showIcon
              closable
              onClose={() => setDeploySuccess(false)}
              className={styles.SuccessAlert}
              style={{ marginBottom: 16 }}
            />
          )}

          <p className={styles.Subtitle}>{t('Subtitle')}</p>

          <Spin
            spinning={loading}
            indicator={<LoadingOutlined spin style={{ fontSize: 24 }} />}
          >
            {/* Target Environment */}
            <div className={styles.FormSection}>
              <label className={styles.Label}>
                {t('Label.TargetEnvironment')}{' '}
                <span className={styles.Required}>*</span>
              </label>
              <div className={styles.EnvironmentSelectContainer}>
                <Select
                  placeholder={t('Placeholder.SelectEnvironment')}
                  value={selectedEnvironmentId || undefined}
                  onChange={setSelectedEnvironmentId}
                  style={{ width: '100%' }}
                  size='large'
                  popupClassName={styles.SelectDropdown}
                  suffixIcon={<Icon component={ServerIcon} />}
                  notFoundContent={t('EnvironmentDropdown.NoEnvironmentsFound')}
                >
                  {environments.map((env) => (
                    <Option key={env.id} value={env.id}>
                      {env.name}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Replicas */}
            <div className={styles.FormSection}>
              <div className={styles.ReplicasContainer}>
                <div className={styles.ReplicasInputSection}>
                  <label className={styles.Label}>{t('Label.Replicas')}</label>
                  <InputNumber
                    min={1}
                    value={replicas}
                    onChange={(value) => setReplicas(value || 1)}
                    style={{ width: '100%' }}
                    size='large'
                  />
                  <p className={styles.HelpText}>{t('HelpText.Replicas')}</p>
                </div>
                <Card className={styles.ResourceAllocationCard}>
                  <div className={styles.ResourceAllocationContent}>
                    <InfoCircleOutlined className={styles.InfoIcon} />
                    <div>
                      <div className={styles.ResourceAllocationTitle}>
                        {t('ResourceAllocation.Title')}
                      </div>
                      <div className={styles.ResourceAllocationDescription}>
                        {t('ResourceAllocation.Description')}
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>

            {/* Encryption Key (Optional) */}
            <div className={styles.FormSection}>
              <label className={styles.Label}>
                <SafetyOutlined className={styles.ShieldIcon} />
                {t('Label.EncryptionKey')}{' '}
                <span className={styles.Optional}>({tCommon('Optional')})</span>
              </label>
              <Card className={styles.SecureTransitCard}>
                <div className={styles.SecureTransitHeader}>
                  <KeyOutlined className={styles.SecureTransitIcon} />
                  <div>
                    <div className={styles.SecureTransitTitle}>
                      {t('SecureTransit.Title')}
                    </div>
                    <div className={styles.SecureTransitDescription}>
                      {t('SecureTransit.Description')}
                    </div>
                  </div>
                </div>
                <div className={styles.EncryptionKeyFields}>
                  <div className={styles.EncryptionKeyField}>
                    <label className={styles.FieldLabel}>
                      {t('Label.Context')}
                    </label>
                    <Select
                      placeholder={t('Placeholder.SelectContext')}
                      value={encryptionContext || undefined}
                      onChange={handleContextChange}
                      style={{ width: '100%' }}
                      popupClassName={styles.SelectDropdown}
                      notFoundContent={t('ContextDropdown.NoContextsFound')}
                    >
                      {availableContexts.map((contextName) => (
                        <Option key={contextName} value={contextName}>
                          {contextName}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div className={styles.EncryptionKeyField}>
                    <label className={styles.FieldLabel}>
                      {t('Label.SecretKey')}
                    </label>
                    <Select
                      placeholder={t('Placeholder.SelectSecret')}
                      value={encryptionKey || undefined}
                      onChange={setEncryptionKey}
                      style={{ width: '100%' }}
                      disabled={!encryptionContext}
                      popupClassName={styles.SelectDropdown}
                      notFoundContent={t('Placeholder.NoSecretsFound')}
                    >
                      {availableEnvVars.map((envVarInfo) => (
                        <Option key={envVarInfo.key} value={envVarInfo.key}>
                          {envVarInfo.key.toUpperCase()}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </Card>
            </div>

            {/* Notes (Optional) */}
            <div className={styles.FormSection}>
              <label className={styles.Label}>
                {t('Label.Notes')}{' '}
                <span className={styles.Optional}>({tCommon('Optional')})</span>
              </label>
              <Input.TextArea
                placeholder={t('Placeholder.Notes')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                style={{ width: '100%' }}
                maxLength={500}
                showCount
              />
              <p className={styles.HelpText}>{t('HelpText.Notes')}</p>
            </div>
          </Spin>
        </div>
      </RoundedModal>
    </>
  );
};

export default DeployRecipeModal;
